import type { ReactNode } from 'react';
import {
  ChartLineData,
  DataCheck,
  DocumentImport,
  Launch,
  Money,
  ProgressBarRound,
  Settings,
  Tools,
  WarningAlt,
} from '@carbon/react/icons';
import routes from '~/routes';
import type { AdminDashboardConfig, ExternalLink, InternalLink } from './types';
import { getDatadogKafkaUrl, getHerokuUrl, getRollbarUrl } from './utils';

export interface LinkSection {
  icon: ReactNode;
  title: string;
  links: (InternalLink | ExternalLink)[];
  // Tag text for a disabled tile, and whether enabled tiles show the launch icon.
  disabledLabel?: string;
  showLaunchIcon?: boolean;
}

const EVAL_MONITORING_LINKS: InternalLink[] = [
  {
    label: 'Eval2 Runs Status',
    href: routes.adminEval2RunsStatusPath,
    enabled: true,
    description: 'Package evaluation run status and metrics',
  },
  {
    label: 'Legacy Unit Evaluation Downloads',
    href: routes.adminLegacyUnitEvaluationDownloadsPath,
    enabled: true,
    description:
      'Read-only list of exports from the retired unit evaluation; opens existing files only',
  },
  {
    label: 'Blob Drain Status',
    href: routes.adminBlobDrainStatusPath,
    enabled: true,
    description: 'Evaluation output blob migration progress and queue status',
  },
];

const AGENT_COST_LINKS: InternalLink[] = [
  {
    label: 'Agent Cost',
    href: routes.adminAgentCostsPath,
    enabled: true,
    description:
      'Anthropic agent run cost, volume, and success rate by agent type',
  },
];

const PETRO_GEO_LINKS: InternalLink[] = [
  {
    label: 'Well Spacing Job Status',
    href: routes.adminWellSpacingJobStatusPath,
    enabled: true,
    description:
      'Well spacing job monitoring — triggered by new wellbore geometries and geometry updates',
  },
];

const PETRO_ENGINEERING_LINKS: InternalLink[] = [
  {
    label: 'Inventory Automation Status',
    href: routes.adminInventoryAutomationStatusPath,
    enabled: true,
    description:
      'Why a unit has or has not been rebuilt by inventory automation, with the deferral reason for every recompute',
  },
];

const NRI_MONITORING_LINKS: InternalLink[] = [
  {
    label: 'NRI Diagnostics',
    href: routes.adminNriDiagnosticsPath,
    enabled: true,
    description: 'NRI calculation diagnostics',
  },
  {
    label: 'Duplicate NRI Calculations',
    href: routes.adminDuplicateNriCalculationsPath,
    enabled: true,
    description: 'Well/subtract pairs with multiple NRI records',
  },
  {
    label: 'Duplicate Parameters',
    href: routes.adminDuplicateParametersPath,
    enabled: true,
    description:
      'Coverage and analysis parameters with duplicate (subtract_id, unit_id) combinations',
  },
  {
    label: 'Subtracts Missing TIRs',
    href: routes.adminSubtractsMissingTirsPath,
    enabled: true,
    description:
      'Subtracts with geometry but no TractIntersectionResults — invisible to Eval 3.0 NRI calculator',
  },
  {
    label: 'Stale Records Audit',
    href: routes.adminStaleRecordsAuditPath,
    enabled: true,
    description:
      'System-wide dry-run audit of stale TIR and NRI records across all subtract IDs',
  },
  {
    label: 'BoS Drift Audit',
    href: routes.adminBosDriftAuditPath,
    enabled: true,
    description:
      'BundleOfSticksOwnership rows whose stored values drift from their parent Salesforce::Subtract / Salesforce::Tract — a canary for contaminated ownership rows',
  },
];

const DATA_QUALITY_LINKS: InternalLink[] = [
  {
    label: 'Unit Type Mismatches',
    href: routes.adminUnitTypeMismatchesQueryPath,
    enabled: true,
    description: 'Development areas with mismatched unit types',
  },
  {
    label: 'Allocation Manager Checks',
    href: routes.adminAllocationManagerChecksPath,
    enabled: true,
    description:
      'Lateral length allocation managers with missing UAT lateral values',
  },
  {
    label: 'Empty Allocation Managers',
    href: routes.adminEmptyAllocationManagersQueryPath,
    enabled: true,
    description:
      'Allocation managers with zero line items — wells silently produce no NRI',
  },
  {
    label: 'Missing Subtract BOS',
    href: routes.adminMissingSubtractBosPath,
    enabled: true,
    description:
      'Subtracts missing Bundle of Sticks where the parent tract has one',
  },
  {
    label: 'Orphaned Subtract Geometries',
    href: routes.adminOrphanedSubtractGeometriesPath,
    enabled: true,
    description:
      'Subtract geometries where the subtract has been deleted or never existed — GIS team should review',
  },
  {
    label: 'Duplicate Geometries',
    href: routes.adminDuplicateGeometriesPath,
    enabled: true,
    description:
      'Duplicate tract and subtract geometries — GIS team needs to deduplicate or fix source data',
  },
  {
    label: 'Non-Intersecting Explicit Assignments',
    href: routes.adminNonIntersectingExplicitAssignmentsPath,
    enabled: true,
    description:
      "Wells explicitly assigned to units they don't spatially intersect — either the assignment or the geometry is wrong",
  },
  {
    label: 'Unit Well Assignments',
    href: routes.adminUnitWellAssignmentsPath,
    enabled: true,
    description:
      "Every current unit well assignment, with the percent of the well's producing segment inside its assigned unit — filter to the ones under 90%",
  },
  {
    label: 'Missing Well Assignments',
    href: routes.adminMissingWellAssignmentsPath,
    enabled: true,
    description:
      'Wells that intersect units but have no assignment — could indicate missing/bad well geometry or unit boundaries',
  },
  {
    label: 'FOUCS Diagnosis',
    href: routes.adminFoucsDiagnosisPath,
    enabled: true,
    description:
      'Subtracts with no valid horizontal coverage — often caused by missing or misaligned unit geometries',
  },
  {
    label: 'UATs Without Allocation Manager Line Items',
    href: routes.adminUatsWithoutAmlisPath,
    enabled: true,
    description:
      'UATs with no allocation manager line items — TIA cannot discover them for TIR creation until line items arrive',
  },
  {
    label: 'Over-Claimed Slots',
    href: routes.adminOverAllocatedSlotsPath,
    enabled: true,
    description:
      'Slots whose owner interests sum past 100% — start here to see which units are affected and why',
  },
  {
    label: 'Subtract Acreage Drift',
    href: routes.adminSubtractAcreageDriftPath,
    enabled: true,
    description:
      'Subtracts whose recorded gross acreage cannot be right — gross below net, or contradicted by the polygon. Gross divides every interest, so an understated one inflates it',
  },
];

/**
 * The dashboard's link sections, in display order, for this environment.
 * @param {AdminDashboardConfig} config - The server-rendered dashboard config
 * @param {boolean} bulkDedupeEnabled - Whether the bulk-dedupe flag is on
 * @return {LinkSection[]} The sections
 */
export function linkSections(
  config: AdminDashboardConfig,
  bulkDedupeEnabled: boolean
): LinkSection[] {
  const docIngestionLinks: InternalLink[] = [
    {
      label: 'Bulk Provision',
      href: routes.adminProvisioningPath,
      enabled: true,
      description:
        'Bulk provisioning of existing documents with LLM extraction',
    },
    {
      label: 'Prompt Management',
      href: routes.adminPromptManagementPath,
      enabled: true,
      description:
        'View extraction and agent prompts, their version history, and usage',
    },
    {
      label: 'Auto-Match Configuration',
      href: routes.adminAutoMatchConfigurationPath,
      enabled: true,
      description:
        'Tune the entity auto-match gate: thresholds, per-guardrail toggles, enabled counties, and marker lists',
    },
    {
      label: 'Entity Library Clean-up Log',
      href: routes.adminEntityCleanupLogPath,
      enabled: true,
      description:
        'Every merge action against the entity library: who retired which entity into what, with the match signals behind it',
    },
    // Hidden unless FEATURE_FLAG_BULK_DEDUPE is enabled (default off).
    ...(bulkDedupeEnabled
      ? [
          {
            label: 'Bulk Dedupe',
            href: routes.adminBulkDedupePath,
            enabled: true,
            description:
              'Normalize addresses, generate exact-name entity clusters, review, and merge',
          },
        ]
      : []),
  ];

  const systemAdminLinks: InternalLink[] = [
    {
      label: 'Health Check (JSON)',
      href: '/admin/health',
      enabled: true,
      description: 'View raw health check response',
    },
    {
      label: 'Sidekiq Dashboard',
      href: '/admin/sidekiq',
      enabled: config.sidekiqEnabled,
      description: 'Background job processing dashboard',
    },
    {
      label: 'Karafka UI',
      href: '/admin/karafka',
      enabled: config.karafkaEnabled,
      description: 'Kafka consumer monitoring',
    },
    {
      label: 'Scheduled Jobs',
      href: routes.adminScheduledJobsPath,
      enabled: true,
      description: 'View and trigger scheduled jobs',
    },
    {
      label: 'Sidekiq Queue Assignments',
      href: routes.adminSidekiqQueueAssignmentsPath,
      enabled: true,
      description:
        'Which background job runs on which Sidekiq queue, honoring live queue overrides',
    },
    {
      label: 'Intersection Coordination Queue',
      href: routes.adminIntersectionCoordinationQueuePath,
      enabled: true,
      description:
        'Monitor queue depth and job type breakdown for intersection coordination',
    },
    {
      label: 'Sidekiq Timed Queues',
      href: routes.adminSidekiqTimedQueuesPath,
      enabled: true,
      description:
        'Depth, latency and job classes for the timed queues, plus running jobs and the retry and dead sets',
    },
  ];

  const externalLinks: ExternalLink[] = [
    {
      label: 'Heroku',
      href: getHerokuUrl(config.environment.label),
      enabled: getHerokuUrl(config.environment.label) !== null,
      description: 'Application dashboard and dynos',
    },
    {
      label: 'Datadog Kafka Consumer Lag',
      href: getDatadogKafkaUrl(config.environment.label),
      enabled: getDatadogKafkaUrl(config.environment.label) !== null,
      description: 'Kafka consumer lag monitoring',
    },
    {
      label: 'Rollbar',
      href: getRollbarUrl(config.environment.label),
      enabled: getRollbarUrl(config.environment.label) !== null,
      description: 'Error tracking and monitoring',
    },
  ];

  return [
    {
      icon: <ChartLineData />,
      title: 'Eval Monitoring',
      links: EVAL_MONITORING_LINKS,
    },
    { icon: <Money />, title: 'Agent / AI Cost', links: AGENT_COST_LINKS },
    { icon: <ProgressBarRound />, title: 'Petro Geo', links: PETRO_GEO_LINKS },
    {
      icon: <Tools />,
      title: 'Petro Engineering',
      links: PETRO_ENGINEERING_LINKS,
    },
    { icon: <WarningAlt />, title: 'Data Quality', links: DATA_QUALITY_LINKS },
    { icon: <DataCheck />, title: 'Data QC', links: NRI_MONITORING_LINKS },
    {
      icon: <DocumentImport />,
      title: 'Doc Ingestion',
      links: docIngestionLinks,
    },
    { icon: <Settings />, title: 'System Admin', links: systemAdminLinks },
    {
      icon: <Launch />,
      title: 'External Monitoring',
      links: externalLinks,
      disabledLabel: 'Unavailable',
      showLaunchIcon: true,
    },
  ];
}
